import React, { useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { nonNumberHeaders } from "./util";
import { find } from "lodash";
import { Link } from "react-router-dom";
import qs from "query-string";
import { XeroClient } from "services";
import { LinkCells } from "./ReportRowsTrialB";
import LoadingMsg from "components/common/LoadingMsg";
import { isNumericString } from "services/util";


const ReportLink = ({ name, params, children, ...props }) => {
  return (
    <Link
      to={{
        pathname: `/reports/${name}`,
        search: qs.stringify(params),
      }}
      {...props}
    >
      {children}
    </Link>
  );
};

const ReportCell = ({ value, index, rowType, header, boldRow }) => {
  const nonNumberHeader = nonNumberHeaders[header?.Value] || !index;
  const { Attributes } = value;

  const { report, link } = useMemo(() => {
    const report = find(Attributes, { Id: "report" })?.Value;
    const link = find(Attributes, { Id: "link" })?.Value;

    return {
      report,
      link,
    };
  }, [Attributes]);

  if (rowType === "Header") {
    return (
      <th className={!nonNumberHeader ? "text-right" : "text-left"}>{value.Value}</th>
    );
  } else {
    return (
      <td
        className={cx(
          rowType === "SummaryRow" || boldRow ? "font-weight-bold" : "",
          !nonNumberHeader ? "text-right" : "",
          "text-left"
        )}
        style={{
          fontSize: "0.8rem",
          lineHeight: "0.8rem",
          textIndent: "0.75rem",
        }}
      >{value.Value ? (        
        report ? (
          <ReportLink {...report}>{value.Value}</ReportLink>
        ) : link ? (
          <Link to={link}>{value.Value}</Link>
        ) : (
          value.Value
        )
      ) : (       
        "-"
      )}
    </td>
    );
  }
};

const ReportRow = ({ value, headers }) => {
  const { RowType, Cells, Title, Rows } = value;

  if (RowType === "Header") {
    return (
      <tr className="table-info">
        {Cells.map((i, idx) => (
          <ReportCell
            index={idx}
            value={i}
            rowType={RowType}
            key={idx}
            header={headers[idx]}
          />
        ))}
      </tr>
    );
  } else if (RowType === "Row" || RowType === "SummaryRow") {
    const isNetAsset = Cells[0].Value==="Net Assets"
    return (
      <tr>
        {Cells.map((i, idx) => (
          <ReportCell
            index={idx}
            value={i}
            rowType={RowType}
            key={idx}
            header={headers[idx]}
            boldRow={isNetAsset}
          />
        ))}
      </tr>
    );
  } else if (RowType === "Section") {
    return (
      <>
        {Title ? (
          <tr>
            <td colSpan={headers.length} className="text-left" style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              {Title}
            </td>
          </tr>
        ) : null}
        {Rows?.map((i, idx) => (
          <ReportRow key={idx} value={i} headers={headers} />
        ))}
      </>
    );
  } else if (RowType === "BigHead") {
    return (
      <tr>
        <td
          colSpan={headers.length}
          style={{ fontWeight: "bold", lineHeight: "1.75rem" }}
        >
          {Title}
        </td>
        {Rows?.map((i, idx) => (
          <ReportRow key={idx} value={i} headers={headers} />
        ))}
      </tr>
    );
  } else {
    return null;
  }
};

export const ReportRows = ({ rows }) => {

  const [header, ...rest] = rows;
  const headers = header.Cells;

  const titlesTrials = ["Revenue", "Assets", "Liabilities"];
  const titlesBalance = ["Bank", "Current Assets"];
  const titlesPL = ["Less Cost of Sales", "Less Operating Expenses", "Income"];
  const titlesExec = ["Cash", "Profitability"]

  const trialBalance = rest.some(section => titlesTrials.includes(section?.Title)) && rest.every(section => !titlesBalance.includes(section?.Title));
  const pAndL = rest.some(section => titlesPL.includes(section?.Title) && rest.every(section => !titlesExec.includes(section?.Title)));
  const balanceSheet = rest.some(section => titlesBalance.includes(section?.Title));

  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);

  const fetchAccounts = async () => {
    const xero = XeroClient();
    try {
      const res = await xero.get("accounts");
      setData(res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    setLoading(true)
    const fetchData = async () => {
      if (balanceSheet || pAndL) {
        await fetchAccounts();
      }
      setLoading(false)
    };
    fetchData();
  }, [balanceSheet]);

  if (!rows.length) return null;

  const processTrialBalance = () => {
    const newHeader = { Value: 'Code', NonNumber: true };
    headers.splice(1, 0, newHeader);

    rest.forEach(section => {
      const rows = section.Rows;
      if (rows) {
        rows.forEach(row => {
          const cells = row.Cells;
          if (cells && cells.length > 1) {
            const valueParts = cells[0].Value.split(' ');
            const lastPart = valueParts.length > 1 && valueParts[valueParts.length - 1].startsWith('(')
              ? valueParts.pop()
              : '';
            
            const remainingPart = valueParts.join(' ');
            const accountCode = lastPart ? lastPart : '-';
            cells.splice(0, 1, { Value: accountCode, NonNumber: true });
            cells.unshift({ Value: remainingPart, NonNumber: true });
          }
        });
      }
    });
  };

  const processBalanceSheetPnL = () => {
    if (!headers.some(header => header.Value === 'Code') && data) {
      const newHeader = { Value: 'Code', NonNumber: true };
      headers.splice(1, 0, newHeader);

      rest.forEach(section => {
        const rows = section.Rows;
        if (rows) {
          rows.forEach(row => {
            const cells = row.Cells;
            if (cells && cells.length > 1) {
              const accountName = cells[0].Value;
              const getCode = data.find(item => item.Name === accountName)?.Code;
              const accountCode = getCode && isNumericString(getCode) ? `(${getCode})` : '-';

              cells.splice(0, 1, { Value: accountCode, NonNumber: true });
              cells.unshift({ Value: accountName, NonNumber: true });
            }
          });
        }
      });
    }
  };

  if (trialBalance) {
    processTrialBalance();
  } else if (balanceSheet || pAndL) {
    processBalanceSheetPnL();
  } else {
    console.log("Skipping as it is not Balance Sheet, Profit and Loss and neither Trial Balance.");
  }

  return (
    <>
    {!loading ? 
      <>
        <ReportRow value={header} headers={headers} />
        {rest.map((i, idx) => (
          <ReportRow value={i} key={idx} headers={headers} />
        ))}
      </> 
      :<LoadingMsg/>
    }
    </>
  );
};
